import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@ibsys/kit';
import type {
  AllDetailsFiltersState,
  AllDetailsSortState,
  DashboardQualitySettings,
  QualityColumnDescriptor,
  QualityDetailItem,
} from '@/entities/dashboards';
import type { ConversationAttributeItem } from '@/entities/attributes';
import { AllDetailsTable } from './AllDetailsTable';

type Props = {
  items: QualityDetailItem[];
  blockNames: string[];
  isLoading: boolean;
  /** Описания доступных колонок из ответа quality-details */
  availableColumns: QualityColumnDescriptor[];
  /** Системная конфигурация атрибутов разговора (для форматирования ячеек атрибутных колонок) */
  availableAttributes: ConversationAttributeItem[];
  /** Настройки видимости и порядка колонок из uiSettings */
  columnSettings: DashboardQualitySettings['allDetailsColumns'];
  /** Сохранение настроек колонок (visibleColumns + columnOrder) */
  onChangeColumnSettings: (next: { visibleColumns: string[]; columnOrder: string[] }) => void;
  /** Текущая серверная сортировка таблицы. */
  sort: AllDetailsSortState | null;
  /** Колбэк изменения серверной сортировки. */
  onSortChange: (next: AllDetailsSortState | null) => void;
  /** Текущие серверные фильтры таблицы. */
  filters: AllDetailsFiltersState;
  /** Колбэк изменения серверных фильтров. */
  onFiltersChange: (next: AllDetailsFiltersState) => void;
  /** Открытие диалога строки; `openInNewTab` — пользователь кликнул с shift */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Карточка-обёртка таблицы всех деталей дашборда качества. Сортировка и
 * фильтрация выполняются на бэкенде через query-параметры `sort` и `filter-*`
 * эндпоинта `GET /dashboard/quality-details`. Состав и порядок колонок
 * настраиваются через панель и сохраняются в
 * `uiSettings.dashboardQuality.allDetailsColumns`.
 */
export const AllDetailsCard: FC<Props> = ({
  items,
  blockNames,
  isLoading,
  availableColumns,
  availableAttributes,
  columnSettings,
  onChangeColumnSettings,
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  onOpenConversation,
}) => {
  const { t } = useTranslation('qualityDashboard');

  // При смене набора динамических колонок (критериев выборки) пересоздаём
  // компонент, чтобы сбросить локальное состояние сортировки/скролла.
  const columnsKey = blockNames.join(',');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('allDetails.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner className="size-6" />
          </div>
        ) : (
          <AllDetailsTable
            key={columnsKey}
            rows={items}
            blockNames={blockNames}
            availableColumns={availableColumns}
            availableAttributes={availableAttributes}
            columnSettings={columnSettings}
            onChangeColumnSettings={onChangeColumnSettings}
            sort={sort}
            onSortChange={onSortChange}
            filters={filters}
            onFiltersChange={onFiltersChange}
            onOpenConversation={onOpenConversation}
          />
        )}
      </CardContent>
    </Card>
  );
};
